import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, MailQuestion } from "lucide-react";
import { Logo } from "@/components/site/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries.current += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch {
        setState("error");
        return;
      }
      if (tries.current >= 8) { setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FAFAFC] flex flex-col items-center justify-center px-6 py-12">
      <Logo />
      <div className="mt-8 w-full max-w-md rounded-3xl border border-navy-100 bg-white shadow-xl p-8 text-center" data-testid="payment-success-card">
        {state === "checking" && (
          <div className="py-8" data-testid="payment-checking">
            <Loader2 size={34} className="animate-spin mx-auto text-gold-600" aria-hidden />
            <p className="mt-4 font-display text-xl font-semibold text-navy-900">Confirming your payment…</p>
            <p className="mt-2 text-sm text-charcoal-700/70">This usually takes a few seconds. Please don't close this page.</p>
          </div>
        )}
        {state === "paid" && (
          <div className="py-4" data-testid="payment-paid">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <CheckCircle2 size={36} aria-hidden />
            </span>
            <p className="mt-5 font-display text-2xl font-semibold text-navy-900">Payment received — thank you!</p>
            <p className="mt-3 text-charcoal-700 leading-relaxed">
              Your payment is confirmed. The Konek Church team will follow up shortly with your next steps.
            </p>
            <Link to="/" className="mt-6 inline-block rounded-full bg-navy-900 text-white font-semibold px-6 py-3 hover:bg-navy-800 transition-colors" data-testid="payment-home-link">
              Back to konekchurch.com
            </Link>
          </div>
        )}
        {state === "pending" && (
          <div className="py-4" data-testid="payment-pending">
            <MailQuestion size={34} className="mx-auto text-gold-600" aria-hidden />
            <p className="mt-4 font-display text-xl font-semibold text-navy-900">Payment is processing</p>
            <p className="mt-2 text-sm text-charcoal-700/80">
              Your payment is still being confirmed by the bank. You'll receive an email confirmation — no need to pay again.
            </p>
          </div>
        )}
        {state === "error" && (
          <div className="py-4" data-testid="payment-error">
            <p className="font-display text-xl font-semibold text-navy-900">We couldn't confirm this payment</p>
            <p className="mt-2 text-sm text-charcoal-700/80">
              If you completed checkout, don't worry — you'll receive an email confirmation. Otherwise, reopen your payment link to try again, or write to hello@konekchurch.com.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
